import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Y, T, CREDIT, LABEL_FONT } from "../theme";
import { api } from "../firebase";
import { useNotify } from "../App";
import { CreditIcon, CreditAmount } from "./shared";
import PageLoader from "./PageLoader";

export default function BankView() {
  const [loading, setLoading] = useState(true);
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const [welcomeClaimed, setWelcomeClaimed] = useState(true);
  const [claimingWelcome, setClaimingWelcome] = useState(false);

  const navigate = useNavigate();
  const { notify } = useNotify();
  const setError = (msg) => notify("error", msg, 6000);
  const setSuccess = (msg) => notify("success", msg, 3000);

  useEffect(() => {
    api.getSettings()
      .then(data => {
        setCreditBalance(data.creditBalance ?? data.eddyBalance ?? 0);
        setCreditTransactions(data.creditTransactions ?? data.eddyTransactions ?? []);
        setWelcomeClaimed(!!data.welcomeCreditsClaimed);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <style>{`
        @media (max-width: 600px) {
          .bank-balance-label, .bank-balance-value { text-align: left !important; }
        }
      `}</style>
      <PageLoader ready={!loading}/>

      {/* Welcome Credits Banner */}
      {!welcomeClaimed && !loading && (
        <div style={{
          background: `linear-gradient(135deg, ${Y}12 0%, ${Y}06 100%)`,
          border: `1px solid ${Y}40`,
          borderRadius: 14,
          padding: "20px 24px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}>
          <div style={{flex:"1 1 200px"}}>
            <div style={{fontFamily:LABEL_FONT,fontSize:18,fontWeight:700,color:Y,letterSpacing:0.5,marginBottom:4}}>Welcome to Katana AI Studio!</div>
            <div style={{color:T.text2,fontSize:13,lineHeight:1.5}}>Claim your <strong style={{color:CREDIT}}>500 free credits</strong> to get started generating videos.</div>
          </div>
          <button
            disabled={claimingWelcome}
            onClick={async () => {
              setClaimingWelcome(true);
              try {
                const result = await api.claimWelcomeCredits();
                setCreditBalance(result.balance);
                setWelcomeClaimed(true);
                setSuccess(`${result.amount} welcome credits claimed!`);
                // Refresh transactions
                api.getSettings().then(data => {
                  setCreditTransactions(data.creditTransactions ?? []);
                }).catch(() => {});
              } catch (err) {
                setError(err.message);
              } finally {
                setClaimingWelcome(false);
              }
            }}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "10px 24px", borderRadius: 999,
              background: Y, border: "none", color: T.dark,
              cursor: claimingWelcome ? "wait" : "pointer",
              fontFamily: LABEL_FONT, fontSize: 14, fontWeight: 700,
              letterSpacing: 0.5, textTransform: "uppercase",
              opacity: claimingWelcome ? 0.6 : 1,
              transition: "all 0.2s",
            }}>
            <CreditIcon size={18} color={T.dark}/>
            {claimingWelcome ? "Claiming..." : "Claim 500 Free Credits"}
          </button>
        </div>
      )}

      {/* Bank of Katana Card */}
      <div style={{
        background: `linear-gradient(135deg, ${CREDIT}33 0%, ${CREDIT}1A 100%)`,
        border: `1px solid ${CREDIT}66`,
        borderRadius: 16,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
      }}>
        {/* Subtle watermark */}
        <div style={{position:"absolute",top:-20,right:-20,opacity:0.03,pointerEvents:"none"}}>
          <svg width="180" height="180" viewBox="0 0 24 24" fill={CREDIT}>
            <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z"/>
          </svg>
        </div>

          {/* Left — Header + Balance */}
          <div style={{flex:"1 1 0",padding:24,display:"flex",flexDirection:"column"}}>
            {/* Header — stays at top */}
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:0}}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill={CREDIT} style={{flexShrink:0}}>
                <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z"/>
              </svg>
              <div>
                <div style={{color:CREDIT,fontSize:20,fontWeight:800,letterSpacing:"0.02em"}}>Bank of Katana</div>
                <div style={{color:T.text2,fontSize:12,fontWeight:500}}>Financial Services</div>
              </div>
            </div>
            {/* Balance — vertically centered in remaining space */}
            <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",marginTop:16}}>
              <div className="bank-balance-label" style={{color:T.text2,fontSize:11,fontWeight:400,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8,textAlign:"center"}}>Current Balance</div>
              <div className="bank-balance-value" style={{textAlign:"center"}}><CreditAmount amount={creditBalance} size={60} /></div>
            </div>
          </div>

          {/* Right — Purchase Credits */}
          <div style={{flex:"1 1 0",background:T.media,padding:"20px 24px",borderLeft:`1px solid ${T.overlay12}`,display:"flex",flexDirection:"column",alignItems:"flex-start"}}>
            <div style={{color:CREDIT,fontSize:14,fontWeight:800,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Purchase Katana AI Credits</div>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12,width:"fit-content"}}>
              {(() => {
                const tiers = [
                  { credits: 2000, price: 5.35 },
                  { credits: 4000, price: 10.46 },
                  { credits: 8000, price: 20.44 },
                  { credits: 20000, price: 49.51 },
                ];
                const basePPC = tiers[0].price / tiers[0].credits;
                return tiers.map(({ credits, price }, idx) => {
                  const save = idx > 0 ? Math.round((1 - (price / credits) / basePPC) * 100) : 0;
                  return (
                <button key={credits} onClick={() => navigate("/bank/purchase", { state: { credits, price, fee: +(price - credits * 0.002).toFixed(2) } })} className="s-toggle"
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=CREDIT;e.currentTarget.style.transform="translateY(-1px)";e.currentTarget.style.boxShadow=`0 6px 18px ${CREDIT}3D, 0 0 22px ${CREDIT}24`;}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=`${CREDIT}55`;e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow=`0 2px 10px ${CREDIT}1F`;}}
                  style={{
                  padding:"12px 16px",borderRadius:10,
                  // Credit-teal tint via the live-binding CREDIT so the
                  // purchase tiers read as "credits" and look right in both
                  // dark and the light turquoise theme (cohesive with the
                  // balance card's CREDIT gradient) instead of the old
                  // hardcoded brand-yellow wash.
                  background:`linear-gradient(135deg, ${CREDIT}26 0%, ${CREDIT}12 100%)`,
                  border:`1.5px solid ${CREDIT}55`,
                  boxShadow:`0 2px 10px ${CREDIT}1F`,
                  cursor:"pointer",
                  display:"grid",gridTemplateColumns:"1fr auto 1fr",alignItems:"center",gap:12,
                  transition:"all 0.3s ease",
                }}>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                    <div style={{color:CREDIT,fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",lineHeight:1,marginBottom:2}}>Purchase</div>
                    <div style={{display:"flex",alignItems:"center",gap:3}}>
                      <CreditIcon size={22} color={CREDIT}/>
                      <span style={{color:CREDIT,fontWeight:900,fontSize:24,lineHeight:1,letterSpacing:-1}}>{credits.toLocaleString()}</span>
                    </div>
                  </div>
                  <span style={{color:CREDIT,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>for</span>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                    <span style={{color:"#EEFF44",WebkitTextStroke:"1px #33504B",fontWeight:900,fontSize:22,lineHeight:1,letterSpacing:-0.5}}>${price.toFixed(2)}</span>
                    {save > 0 && <div style={{color:T.success,fontSize:8,fontWeight:800,letterSpacing:"0.06em",lineHeight:1,marginTop:3}}>SAVE {save}%</div>}
                  </div>
                </button>
                  );
                });
              })()}
            </div>

          </div>
      </div>

      {/* Banking Activity */}
      <div style={{marginTop:24}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
          <span style={{color:T.text2,fontSize:13,fontWeight:700}}>Banking Activity</span>
        </div>
        {creditTransactions.length > 0 ? (
          <div style={{borderRadius:12,border:`1px solid ${T.overlay08}`,overflow:"hidden"}}>
            {creditTransactions.slice(0, 20).map((t, i) => {
              const isCharge = t.type === 'charge';
              const isGift = t.type === 'gift';
              const isWelcome = t.type === 'welcome';
              return (
                <div key={i} style={{
                  display:"flex",alignItems:"center",gap:10,padding:"10px 14px",
                  borderBottom:i < Math.min(creditTransactions.length, 20) - 1 ? `1px solid ${T.overlay08}` : "none",
                  background:i % 2 === 0 ? T.overlay04 : "transparent",
                }}>
                  {/* Icon */}
                  <div style={{width:24,height:24,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                    background:isCharge?"rgba(255,45,111,0.08)":isWelcome?`${Y}15`:isGift?`${CREDIT}12`:`${CREDIT}12`}}>
                    {isCharge ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FF2D6F" strokeWidth="2" strokeLinecap="round"><polyline points="12 5 12 19"/><polyline points="19 12 12 19 5 12"/></svg>
                    ) : isWelcome ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    ) : isGift ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={CREDIT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6"/><polyline points="12 3 12 15"/><path d="M8 7l4-4 4 4"/></svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={CREDIT} strokeWidth="2" strokeLinecap="round"><polyline points="12 19 12 5"/><polyline points="5 12 12 5 19 12"/></svg>
                    )}
                  </div>
                  {/* Description */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{color:T.text2,fontSize:12,fontWeight:500}}>
                      {isCharge ? `Generation charge` : isWelcome ? "New user claim" : isGift ? "Gift deposit" : "Credits purchase"}
                    </div>
                    <div style={{color:T.text3,fontSize:10,marginTop:1}}>
                      {isCharge && t.model ? t.model : ""}
                      {/* Gifts always present as coming from the brand
                          account, never the individual owner who sent it. */}
                      {isGift ? `from netrunner@katana-ai.studio` : ""}
                    </div>
                  </div>
                  {/* Amount */}
                  <div style={{flexShrink:0,textAlign:"right"}}>
                    <span style={{display:"inline-flex",alignItems:"center",gap:2,color:isCharge?"#FF2D6F":CREDIT,fontSize:12,fontWeight:700}}>
                      {isCharge ? "\u2212" : "+"}<CreditIcon size={11} color={isCharge?"#FF2D6F":CREDIT}/>{Math.round(t.amount).toLocaleString()}
                    </span>
                  </div>
                  {/* Date */}
                  <div style={{flexShrink:0,width:70,color:T.text3,fontSize:10,textAlign:"right"}}>
                    {new Date(t.date).toLocaleDateString()}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{borderRadius:12,border:"1px solid rgba(255,255,255,0.06)",padding:"20px 16px",textAlign:"center"}}>
            <div style={{color:T.text3,fontSize:12}}>No activity yet</div>
            <div style={{color:T.text3,fontSize:10,marginTop:4,opacity:0.6}}>Transactions will appear here when you convert, claim, or use credits</div>
          </div>
        )}
      </div>

    </div>
  );
}
